// Lightweight client-side helpers for culture + IP-based greetings.
// IP lookup via ipapi.co (free tier, no key). Culture detection via a small heuristic lexicon.

const IP_CACHE_KEY = "vp_ipinfo_v1";
const IP_CACHE_TTL_MS = 1000 * 60 * 60 * 6; // 6 hours

export async function fetchLocationHint() {
  try {
    const cached = typeof window !== "undefined" ? sessionStorage.getItem(IP_CACHE_KEY) : null;
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed && Date.now() - (parsed._ts || 0) < IP_CACHE_TTL_MS) return parsed;
    }
  } catch { /* ignore */ }

  try {
    const res = await fetch("https://ipapi.co/json/", { headers: { Accept: "application/json" } });
    if (!res.ok) throw new Error("ipapi");
    const d = await res.json();
    const info = {
      country: d.country_name || d.country || "",
      country_code: d.country_code || "",
      city: d.city || "",
      region: d.region || "",
      timezone: d.timezone || "",
      _ts: Date.now(),
    };
    try { sessionStorage.setItem(IP_CACHE_KEY, JSON.stringify(info)); } catch { /* ignore */ }
    return info;
  } catch {
    return { country: "", country_code: "", city: "", region: "", timezone: "" };
  }
}

export function localTimeOfDay(timezone) {
  try {
    const hour = Number(
      new Intl.DateTimeFormat("en-GB", { hour: "2-digit", hour12: false, timeZone: timezone || undefined })
        .format(new Date())
    );
    if (Number.isNaN(hour)) return "";
    if (hour < 5) return "night";
    if (hour < 12) return "morning";
    if (hour < 17) return "afternoon";
    if (hour < 21) return "evening";
    return "night";
  } catch { return ""; }
}

export function formatLocationHint(info) {
  if (!info) return "";
  const parts = [info.city, info.country].filter(Boolean);
  return parts.join(", ");
}

// Simple culture detection from name. Returns: punjabi | hindu | muslim | western | unknown.
// Based on common Indian name markers. Not exhaustive — meant as a heuristic hint to the LLM.
const PUNJABI_MARKERS = [
  "singh", "kaur", "gill", "sidhu", "dhillon", "bajwa", "brar", "sandhu", "virk",
  "rajvinder", "gurvinder", "gurpreet", "gurjit", "gurmail", "gurnam", "harpreet",
  "harinder", "harvinder", "jaspreet", "jasvinder", "manpreet", "mandeep", "simran",
  "rupinder", "sukhdeep", "tejinder", "amardeep", "bhupinder", "baljit", "baljeet",
  "jaswant", "jarnail", "kuldeep", "lakhbir", "navdeep", "paramjit", "rajinder",
  "sukhwinder", "surjit", "parminder",
];
const MUSLIM_MARKERS = [
  "mohammad", "mohammed", "muhammad", "ahmed", "ahmad", "aslam", "aziz", "hussain",
  "hussein", "sayeed", "saeed", "salim", "sohail", "wasim", "yasin", "yusuf",
  "abdul", "farooq", "fatima", "ayesha", "zainab", "khan", "shaikh", "sheikh",
  "syed", "ansari", "quraishi", "qureshi", "rehman", "rahman", "iqbal",
];
const WESTERN_MARKERS = [
  "peter", "john", "david", "michael", "william", "james", "robert", "richard",
  "charles", "thomas", "daniel", "matthew", "joseph", "sarah", "emma", "emily",
  "olivia", "charlotte", "amelia", "henry", "oliver", "noah", "liam", "lucas",
  "mason", "ethan", "ben", "jack", "harry", "alex", "chris", "mike", "steve",
];

export function detectCultureFromName(rawName) {
  if (!rawName || typeof rawName !== "string") return "unknown";
  const lower = rawName.trim().toLowerCase();
  if (!lower) return "unknown";
  // tokenise
  const tokens = lower.split(/[\s,.]+/).filter(Boolean);
  const anyMatch = (markers) => tokens.some((t) => markers.includes(t));
  if (anyMatch(PUNJABI_MARKERS)) return "punjabi";
  if (anyMatch(MUSLIM_MARKERS)) return "muslim";
  if (anyMatch(WESTERN_MARKERS)) return "western";
  // default: Indian Hindu (covers majority of domestic traffic)
  return "hindu";
}
